import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { fileUpload } from '../../services/ticket';
import { searchObj } from '../../utils/util';

// 平台检测工具函数
const detectPlatform = () => {
    const { gta_version_ios, sdkVersion } = searchObj(window.location.search);
    const version = gta_version_ios || sdkVersion;

    // 检测iOS WebView环境
    const isIOSWebView = !!(window.webkit && window.webkit.messageHandlers);
    // 检测是否支持现代文件API
    const supportsFileAPI = !!window.File && !!window.FileReader;

    return {
        isIOS: isIOSWebView,
        supportsFileAPI,
        useNativeUpload: !isIOSWebView && supportsFileAPI,
        version
    };
};

const UploadDragger = (props) => {
    const {
        accept = 'image/*',
        maxCount = 1,
        placeholder,
        onChange,
        fileList,
        listType = 'text'
    } = props;

    const [imgUrl, setImgUrl] = useState("");
    const [upList, setUpList] = useState(fileList || []);
    const [uploading, setUploading] = useState(false);
    const [platformInfo, setPlatformInfo] = useState({});

    useEffect(() => {
        setPlatformInfo(detectPlatform());
    }, []);

    // 统一的base64转换函数
    const fileToBase64 = useCallback((file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }, []);

    const imgOnPreview = useCallback(() => {
        if (listType === 'picture') {
            return {
                onPreview: async (file) => {
                    if (!file.url && !file.preview) {
                        try {
                            const fileObj = file.originFileObj || file;
                            file.preview = await fileToBase64(fileObj);
                        } catch (error) {
                            console.error('预览图片失败:', error);
                            message.error('预览图片失败');
                            return;
                        }
                    }
                    setImgUrl(file.url || file.preview);
                }
            };
        }
        return {};
    }, [listType, fileToBase64]);

    const base64toFile = useCallback((dataurl, filename = 'file') => {
        try {
            const arr = dataurl.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const suffix = mime.split('/')[1];
            const bstr = atob(arr[1]);
            const n = bstr.length;
            const u8arr = new Uint8Array(n);

            for (let i = 0; i < n; i++) {
                u8arr[i] = bstr.charCodeAt(i);
            }

            return new File([u8arr], `${filename}.${suffix}`, {
                type: mime
            });
        } catch (error) {
            console.error('Base64转换文件失败:', error);
            throw new Error('文件转换失败');
        }
    }, []);

    const validateFile = useCallback((file) => {
        if (accept === 'video/*') {
            const videoTypes = ['video/mp4', 'video/avi', 'video/quicktime'];
            if (!videoTypes.includes(file.type)) {
                message.error(`${file.name}不是.mp4,.avi,.mov文件`);
                return false;
            }
            if (file.size >= 20 * 1024 * 1024) {
                message.error('视频不能大于20M');
                return false;
            }
        } else if (accept === 'image/*') {
            const imageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
            if (!imageTypes.includes(file.type)) {
                message.error(`${file.name}不是.png,.jpg,.gif文件`);
                return false;
            }
        }
        return true;
    }, [accept]);

    const handleFileUpload = useCallback(async (file) => {
        if (!validateFile(file)) {
            return;
        }

        setUploading(true);
        try {
            const res = await fileUpload({ upload_img: file });

            if (res.code === 0) {
                const newFile = {
                    ...file,
                    response: res,
                    uid: Date.now().toString()
                };
                const newFileList = [...upList, newFile].slice(-maxCount);

                setUpList(newFileList);
                onChange({ fileList: newFileList });
            } else {
                message.error(res.msg || '上传失败');
            }
        } catch (error) {
            console.error('上传失败:', error);
            message.error('上传失败，请重试');
        } finally {
            setUploading(false);
        }
    }, [validateFile, upList, maxCount, onChange]);

    const iosUpload = useCallback((base64Data) => {
        try {
            const file = base64toFile(base64Data, `file_${Date.now()}`);
            handleFileUpload(file);
        } catch (error) {
            message.error('文件处理失败');
            setUploading(false);
        }
    }, [base64toFile, handleFileUpload]);

    const iosSelectImage = useCallback(() => {
        if (window.webkit?.messageHandlers?.selectImage) {
            window.webkit.messageHandlers.selectImage.postMessage('');
        } else {
            message.warning('iOS原生方法调用失败');
        }
    }, []);

    const iosSelectMovie = useCallback(() => {
        if (window.webkit?.messageHandlers?.selectMovie) {
            window.webkit.messageHandlers.selectMovie.postMessage('');
        } else {
            message.warning('iOS原生方法调用失败');
        }
    }, []);

    // 安全地设置全局回调函数
    useEffect(() => {
        if (platformInfo.isIOS) {
            const callbackName = accept === 'image/*' ? 'uploadFile' : 'uploadMedia';
            window[callbackName] = iosUpload;

            return () => {
                // 清理全局函数
                if (window[callbackName]) {
                    delete window[callbackName];
                }
            };
        }
    }, [platformInfo.isIOS, accept, iosUpload]);

    const handleBtnClick = useCallback((event) => {
        if (!platformInfo.isIOS) return;

        event.stopPropagation();
        if (upList.length >= maxCount) {
            message.warning(`最多只能上传${maxCount}个文件`);
            return;
        }

        if (accept.includes('image')) {
            iosSelectImage();
        } else if (accept.includes('video')) {
            iosSelectMovie();
        }
    }, [platformInfo.isIOS, upList.length, maxCount, accept, iosSelectImage, iosSelectMovie]);

    const uploadConfig = {
        accept,
        maxCount,
        fileList: platformInfo.isIOS ? upList : fileList,
        listType,
        disabled: uploading,
        customRequest: ({ file, onSuccess, onError }) => {
            if (platformInfo.isIOS) {
                // iOS使用特殊处理，不执行默认上传
                return;
            }

            // 标准文件上传
            fileUpload({ upload_img: file })
                .then(res => {
                    if (res.code === 0) {
                        onSuccess?.(res);
                    } else {
                        onError?.(new Error(res.msg || "上传失败"));
                    }
                })
                .catch(error => {
                    onError?.(error);
                });
        },
        onRemove: (file) => {
            const newFileList = upList.filter(item => item.uid !== file.uid);
            setUpList(newFileList);
            onChange({ fileList: newFileList });
        },
        beforeUpload: (file) => {
            return validateFile(file);
        },
        onChange: (info) => {
            if (!platformInfo.isIOS) {
                onChange?.(info);
            }
        },
        ...imgOnPreview()
    };

    return (
        <>
            <Upload {...uploadConfig}>
                <Button
                    loading={uploading}
                    onClick={platformInfo.isIOS ? handleBtnClick : undefined}
                    icon={<UploadOutlined />}
                    disabled={uploading || (upList.length >= maxCount)}
                >
                    {placeholder || '点击上传文件'}
                </Button>
            </Upload>

            {listType === 'picture' && (
                <Modal
                    title={platformInfo.isIOS ? "IOS Web预览" : "预览"}
                    visible={!!imgUrl}
                    footer={null}
                    onCancel={() => setImgUrl('')}
                    width="80%"
                >
                    <img alt="预览" style={{ width: '100%' }} src={imgUrl} />
                </Modal>
            )}
        </>
    );
};

export default UploadDragger;